import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type { ApiSuccess } from '../models/api-response.model';
import type { Account, User } from '../models/user.model';
import type { Business } from '../models/business.model';
import type { Plan, Subscription } from '../models/plan.model';
import type { UserBusinessPermissions } from '../models/permission.model';
import { BusinessContextService } from './business-context.service';

export interface LoginCredentials {
  email: string;
  password: string;
  device_info?: Record<string, unknown>;
}

export interface RegisterPayload {
  email: string;
  password: string;
  full_name: string;
  business_name: string;
  phone?: string;
}

// GET /auth/me (auth.service.js:401-455 del backend) — SÍ incluye `user` y `business` (el
// negocio activo), no solo account/subscription/plan/permissions como se documentaba antes.
// Es el único endpoint que puede reconstruir la sesión completa a partir de solo un
// accessToken/cookie de refresh válidos, sin nada en memoria todavía (ver restoreSession()).
interface MeResponse {
  user: User;
  account: Account;
  business: Business | null;
  subscription: Subscription;
  plan: Plan;
  permissions: UserBusinessPermissions | null;
}

interface LoginResponse {
  user: User;
  business: Business | null;
  accessToken: string;
}

interface RegisterResponse {
  account: Account;
  user: User;
  business: Business;
  subscription: Subscription;
  accessToken: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly businessContextService = inject(BusinessContextService);

  // accessToken en memoria, NUNCA en localStorage (CLAUDE.md §7 — mitiga robo por XSS).
  // El refreshToken vive solo en la cookie httpOnly `vendia_refresh`; este service jamás la
  // lee. `accessToken` sí es camelCase real en el wire (nombre de variable, no columna).
  // Todo arranca en null: sin sesión restaurada (ver restoreSession()) no hay usuario logueado
  // — con datos quemados esto arrancaba con un usuario mock siempre autenticado, bypasseando
  // authGuard incluso sin login; con backend real eso sería un bug de seguridad real.
  private readonly _currentUser = signal<User | null>(null);
  private readonly _accessToken = signal<string | null>(null);
  private readonly _account = signal<Account | null>(null);
  private readonly _subscription = signal<Subscription | null>(null);
  private readonly _plan = signal<Plan | null>(null);
  // Fuente única de permisos — PermissionService lee este signal en vez de mantener su propia
  // copia, para que un login/register real siempre quede reflejado en un solo lugar.
  private readonly _permissions = signal<UserBusinessPermissions | null>(null);

  readonly currentUser = this._currentUser.asReadonly();
  readonly accessToken = this._accessToken.asReadonly();
  readonly account = this._account.asReadonly();
  readonly subscription = this._subscription.asReadonly();
  readonly plan = this._plan.asReadonly();
  readonly permissions = this._permissions.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);

  // Comparte un único POST /auth/refresh en vuelo: si varias requests fallan con 401 en
  // paralelo (ej. la página dispara 3 llamadas a la vez tras expirar el token), todas deben
  // esperar el mismo resultado en vez de disparar 3 refresh simultáneos contra el backend.
  private _refreshInFlight$: Observable<string> | null = null;

  // Deliberado: /auth/login/user, NO /auth/login. El mockup es un único formulario que no
  // pregunta "¿eres el dueño o un usuario invitado?"; /auth/login/user busca por users.email
  // directo y sirve para ambos casos, mientras que /auth/login solo funciona si el email es el
  // de accounts.email (dueño). Esa respuesta no trae `account` — se completa con fetchMe().
  login(credentials: LoginCredentials): Observable<User> {
    return this.http.post<ApiSuccess<LoginResponse>>(`${environment.apiUrl}/auth/login/user`, credentials).pipe(
      map((res) => res.data),
      tap(({ user, business, accessToken }) => {
        this._currentUser.set(user);
        this._accessToken.set(accessToken);
        if (business) {
          this.businessContextService.setBusinesses([business]);
          this.businessContextService.setActiveBusiness(business.id);
        }
      }),
      switchMap(({ user }) => this.fetchMe().pipe(map(() => user))),
    );
  }

  register(payload: RegisterPayload): Observable<{ account: Account; user: User; business: Business; subscription: Subscription }> {
    return this.http.post<ApiSuccess<RegisterResponse>>(`${environment.apiUrl}/auth/register`, payload).pipe(
      map((res) => res.data),
      tap(({ account, user, business, subscription, accessToken }) => {
        this._currentUser.set(user);
        this._accessToken.set(accessToken);
        this._account.set(account);
        this._subscription.set(subscription);
        this.businessContextService.setBusinesses([business]);
        this.businessContextService.setActiveBusiness(business.id);
      }),
      // register() no trae `plan` (el objeto completo de subscription_plans con los flags
      // has_advanced_reports etc.) ni `permissions` — sin fetchMe() plan.html mostraría
      // undefined en los flags de feature.
      switchMap(({ account, user, business, subscription }) =>
        this.fetchMe().pipe(map(() => ({ account, user, business, subscription }))),
      ),
    );
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http
      .post<ApiSuccess<{ message: string }>>(`${environment.apiUrl}/auth/forgot-password`, { email })
      .pipe(map((res) => res.data));
  }

  resetPassword(token: string, password: string): Observable<{ message: string }> {
    return this.http
      .post<ApiSuccess<{ message: string }>>(`${environment.apiUrl}/auth/reset-password`, { token, password })
      .pipe(map((res) => res.data));
  }

  // POST /auth/logout invalida la sesión del lado del servidor y limpia la cookie. Manda la
  // request ANTES de limpiar el estado local (no después): authInterceptor lee
  // accessToken() en el momento en que esta request pasa por él, así que si clearSession()
  // corriera primero, /auth/logout saldría siempre SIN Authorization → 401 "Token requerido"
  // real, sin haber invalidado nunca la sesión correcta en el servidor. El estado local se
  // limpia igual de inmediato después (no espera la respuesta) — logout debe sentirse
  // instantáneo y funcionar incluso si la request de red falla.
  logout(): void {
    this.http.post(`${environment.apiUrl}/auth/logout`, {}).subscribe();
    this.clearSession();
  }

  // POST /auth/refresh con withCredentials:true (agregado por authInterceptor a toda request) —
  // usa la cookie httpOnly `vendia_refresh` para obtener un accessToken nuevo. Cachea el
  // Observable en curso con shareReplay(1) hasta que complete: así, si 3 requests en paralelo
  // disparan un 401 al mismo tiempo, las 3 comparten el mismo refresh en vez de triplicarlo.
  refreshToken(): Observable<string> {
    if (this._refreshInFlight$) {
      return this._refreshInFlight$;
    }

    this._refreshInFlight$ = this.http.post<ApiSuccess<{ accessToken: string }>>(`${environment.apiUrl}/auth/refresh`, {}).pipe(
      map((res) => res.data.accessToken),
      tap((accessToken) => this._accessToken.set(accessToken)),
      finalize(() => {
        this._refreshInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this._refreshInFlight$;
  }

  // Se llama al bootstrap de la app (provideAppInitializer en app.config.ts), antes de
  // renderizar nada: intenta reconstruir la sesión desde la cookie httpOnly `vendia_refresh`
  // (sin ella, /auth/refresh responde 401 y no hay nada más que hacer). Un usuario sin sesión
  // válida no debe ver ningún mensaje de error al abrir la app, por eso el catchError absorbe
  // el fallo en vez de propagarlo.
  restoreSession(): Observable<void> {
    return this.refreshToken().pipe(
      switchMap(() => this.fetchMe()),
      map(() => undefined),
      catchError((err: unknown) => {
        // Mismo criterio que authInterceptor: solo un 401 real de /auth/refresh significa
        // "sesión inválida". Un error de red/5xx al arrancar (backend reiniciando por un
        // deploy) no es eso — no hay nada que limpiar (todo arranca en null igual), pero
        // tampoco hay que confirmar la sesión como muerta por un fallo transitorio.
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.clearSession();
        }
        return of(undefined);
      }),
    );
  }

  private clearSession(): void {
    this._currentUser.set(null);
    this._accessToken.set(null);
    this._account.set(null);
    this._subscription.set(null);
    this._plan.set(null);
    this._permissions.set(null);
    this.businessContextService.clear();
  }

  // GET /auth/me — hidrata TODO el estado de sesión de una vez (incluido currentUser y el
  // negocio activo), no solo lo que login/register no traían. Es el único punto que setea
  // plan/permissions, así que login/register/restoreSession siempre terminan encadenados acá.
  private fetchMe(): Observable<MeResponse> {
    return this.http.get<ApiSuccess<MeResponse>>(`${environment.apiUrl}/auth/me`).pipe(
      map((res) => res.data),
      tap((me) => {
        this._currentUser.set(me.user);
        this._account.set(me.account);
        this._subscription.set(me.subscription);
        this._plan.set(me.plan);
        this._permissions.set(me.permissions);
        if (me.business) {
          this.businessContextService.setBusinesses([me.business]);
          this.businessContextService.setActiveBusiness(me.business.id);
        }
      }),
    );
  }
}
